import { NgModule } from "@angular/core";
import { Routes, RouterModule } from "@angular/router";
import { CollectionComponent } from "./component/collection/collection.component";
import { HomeComponent } from "./component/home/home.component";
import { financeGuard } from "./features/finance/guards/finance.guard";
import { LoginComponent } from "./component/login/login.component";
import { UploadComponent } from "./component/upload/upload.component";
import { DataComponent } from "./component/data/data.component";
import { SingleProjectComponent } from "./component/single-project/single-project.component";
import { AdminAccessComponent } from "./component/admin-access/admin-access.component";
import { AuthGaurdGuard } from "./auth-gaurd.guard";
import { AlertsComponent } from "./component/alerts/alerts.component";
import { PageNotFoundComponentComponent } from "./component/page-not-found-component/page-not-found-component.component";
import { PlayListComponent } from "./component/play-list/play-list.component";
import { SinglePlaylistViewComponent } from "./component/single-playlist-view/single-playlist-view.component";
import { AuthLoginGuard } from "./auth-login.guard";
import { LeadsComponent } from "./component/crm/leads/leads.component";
import { CrmComponent } from "./component/crm/crm.component";
import { DashboardComponent } from "./component/crm/dashboard/dashboard.component";
import { LeadTrackComponent } from "./component/lead-track/lead-track.component";
import { InventoriesComponent } from "./component/inventory/inventories/inventories.component";
import { InventoryDashboardComponent } from "./component/inventory/inventory-dashboard/inventory-dashboard.component";
import { InventoryComponent } from "./component/inventory/inventory.component";
import { WorkstationComponent } from "./component/inventory/workstation/workstation.component";
import { ServiceRequestComponent } from "./component/inventory/service-request/service-request.component";
import { InventoryRequestComponent } from "./component/inventory/inventory-request/inventory-request.component";
import { NewInventoryComponent } from "./component/inventory/new-inventory/new-inventory.component";
import { AdminRequestComponent } from "./component/inventory/admin-request/admin-request.component";
import { ReturnRequestComponent } from "./component/inventory/return-request/return-request.component";
import { AssetLogsComponent } from "./component/inventory/asset-logs/asset-logs.component";
import { assetLogsGuard } from "./guards/asset-logs.guard";
import { MainUnifiedChatComponent } from "./component/main-unified-chat/main-unified-chat.component";
import { ViewAsChatsComponent } from "./component/view-as-chats/view-as-chats.component";
import { viewAsChatRedirect, viewAsOnly } from "./guards/view-as-chat.guard";
import { CreateProfileComponent } from "./component/create-profile/create-profile.component";
import { ChangePasswordComponent } from "./component/change-password/change-password.component";
import { VaultComponent } from "./component/data-vault/vault/vault.component";
import { KnowledgeBaseComponent } from "./component/data-vault/knowledge-base/knowledge-base.component";
import { VretailLeadsComponent } from "./component/lead-track/vretail-leads/vretail-leads.component";
import { IicLeadsComponent } from "./component/lead-track/iic-leads/iic-leads.component";
import { VestateLeadsComponent } from "./component/lead-track/vestate-leads/vestate-leads.component";
import { OutboundLeadsComponent } from "./component/lead-track/outbound-leads/outbound-leads.component";
import { CalendarComponent } from "./component/lead-track/calendar/calendar.component";
import { AllLeadsComponent } from "./component/lead-track/all-leads/all-leads.component";
import { WhatsappBroadcastComponent } from "./component/whatsapp-broadcast/whatsapp-broadcast.component";

const routes: Routes = [
  { path: "login", component: LoginComponent, canActivate: [AuthLoginGuard] },
  { path: "home", component: HomeComponent, canActivate: [AuthGaurdGuard] },
  { path: "change-password", component: ChangePasswordComponent, canActivate: [AuthGaurdGuard] },
  {
    path: "lead-track",
    component: LeadTrackComponent,
    canActivate: [AuthGaurdGuard],
    data: { module: "LEAD-TRACKING" },
    children :[
      {
        path: "",
        redirectTo: "all-leads",
        pathMatch: "full"
      },
      {
        path: "vretail-leads",
        component:VretailLeadsComponent,
      },
      {
        path: "calendar-view",
        component:CalendarComponent,
      },
      {
        path:"iic-leads",
        component: IicLeadsComponent,
      },
      {
        path:"vestate-leads",
        component: VestateLeadsComponent
      },
      {
        path: "outbound-leads",
        component: OutboundLeadsComponent
      },
      {
        path: "all-leads",
        component: AllLeadsComponent
      }
    ]
  },
  {
    path: "create-profile",
    component: CreateProfileComponent,
    canActivate: [AuthGaurdGuard],
    data: { module: "CREATE-PROFILE" }
  },
  {
    path: "collection",
    component: CollectionComponent,
    canActivate: [AuthGaurdGuard],
    data: { module: "COLLECTIONS" }
  },
  {
    path: "main-chat",
    component: MainUnifiedChatComponent,
    canActivate: [AuthGaurdGuard, viewAsChatRedirect],
  },
  {
    path: "main-chat/:channelId",
    component: MainUnifiedChatComponent,
    canActivate: [AuthGaurdGuard, viewAsChatRedirect],
  },
  {
    // "View as" reads the person's chats here, read-only (2 Nov 2026)
    path: "view-as-chats",
    component: ViewAsChatsComponent,
    canActivate: [AuthGaurdGuard, viewAsOnly],
  },
  {
    path: "crm",
    component: CrmComponent,
    canActivate: [AuthGaurdGuard],
    data: { module: "CRM" },
    children: [
      {
        path: "dashboard",
        component: DashboardComponent,
      },
      {
        path: "leads",
        component: LeadsComponent,
      },
    ],
  },
  {
    path: "inventory",
    component: InventoryComponent,
    canActivate: [AuthGaurdGuard],
    data: { module: "INVENTORY" },
    children: [
      {
        path: "dashboard",
        component: InventoryDashboardComponent,
      },
      {
        path: "inventories",
        component: InventoriesComponent,
      },
      {
        path: "workstation",
        component: WorkstationComponent,
      },
      {
        path: "service-request",
        component: ServiceRequestComponent,
      },
      {
        path: "inventory-request",
        component: InventoryRequestComponent,
      },
      {
        path: "new-inventory-request",
        component: NewInventoryComponent,
      },
      {
        path: "return-request",
        component: ReturnRequestComponent,
      },
      {
        path: "admin-request",
        component: AdminRequestComponent,
      },
      {
        path: "asset-logs",
        component: AssetLogsComponent,
        canActivate: [assetLogsGuard],
      },
    ],
  },
  {
    path: "ai",
    loadChildren: () =>
      import("./features/ai/ai.module").then((m) => m.AiModule),
    canActivate: [AuthGaurdGuard],
    data: { module: "LINK-AI" }
  },
  { 
    path: "upload", 
    component: UploadComponent, 
    canActivate: [AuthGaurdGuard],
    data: { module: "ARCHIVE" }
  },
  // The knowledge base is for every signed-in person (no VAULT module needed); Media keeps the gate.
  {
    path: "vault/knowledge",
    component: KnowledgeBaseComponent,
    canActivate: [AuthGaurdGuard]
  },
  {
    path: "vault/knowledge/folder/:folderId",
    component: KnowledgeBaseComponent,
    canActivate: [AuthGaurdGuard]
  },
  { 
    path: "vault", 
    component: VaultComponent, 
    canActivate: [AuthGaurdGuard],
    data: { module: "VAULT" }
  },
  {
    path: "finance",
    loadChildren: () =>
      import("./features/finance/finance.module").then((m) => m.FinanceModule),
    canActivate: [AuthGaurdGuard, financeGuard],
    data: { module: "FINANCE" }
  },
  {
    path: "whatsapp-broadcast",
    component: WhatsappBroadcastComponent,
    canActivate: [AuthGaurdGuard],
    data: { module: "WHATSAPP-BROADCAST" }
  },
  {
    path: "edit/:id",
    component: UploadComponent,
    canActivate: [AuthGaurdGuard],
    data: { module: "ARCHIVE" }
  },
  { 
    path: "data", 
    component: DataComponent, 
    canActivate: [AuthGaurdGuard] 
  },
  {
    path: "collection/play-list",
    component: PlayListComponent,
  },
  {
    path: "single-playlist/:id",
    component: SinglePlaylistViewComponent,
  },
  {
    path: "single/:id",
    component: SingleProjectComponent,
    canActivate: [AuthGaurdGuard],
  },
  {
    path: "access",
    component: AdminAccessComponent,
    canActivate: [AuthGaurdGuard],
  },
  { path: "alerts", component: AlertsComponent },
  {
    path: "link-workspace",
    loadComponent: () => import("./component/link-workspace/link-workspace.component").then(m => m.LinkWorkspaceComponent),
    canActivate: [AuthGaurdGuard],
    data: { module: "PROJECT-TRACKER" }
  },
  {
    path: "reimbursement",
    loadComponent: () => import("./component/reimbursement/reimbursement.component").then(m => m.ReimbursementComponent),
    canActivate: [AuthGaurdGuard]
  },
  {
    // What's new (14 Oct 2026): version history inside Link.
    path: "whats-new",
    loadComponent: () => import("./component/whats-new/whats-new.component").then((m) => m.WhatsNewComponent),
    canActivate: [AuthGaurdGuard],
  },
  {
    // Tasks (14 Oct 2026): every logged-in user, no module grant needed.
    path: "tasks",
    loadChildren: () => import("./features/tasks/tasks.routes").then((m) => m.TASKS_ROUTES),
    canActivate: [AuthGaurdGuard],
  },
  { path: "", redirectTo: "/login", pathMatch: "full" },
  // { path: 'normal-view', loadChildren: () => import('./normal-view/normal-view/normal-view.module').then(m => m.NormalViewModule) },
  {
    path: "**",
    // redirectTo: "/login",
    // pathMatch: "full",
    component: PageNotFoundComponentComponent,
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: "enabled",
      useHash: true,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {} // rebuild-targets
